import { useCallback, useMemo, useReducer } from 'react';
import {
  type FormSegment,
  type LookalikeFormSegment,
  lookalikeSpecificCategory,
  type SimilarityLevelValue,
} from '../../../../../../types';
import type { UseLookalikeSegmentsSelectionReturn, UseSegmentsSelection } from '../types';
import {
  ActionType,
  type LookalikeDefinitionSegments,
  type LookalikeSegmentsState,
  lookalikeSegmentsReducer,
} from './lookalikeSegmentsReducer';

export const useLookalikeSegmentsSelection: UseSegmentsSelection<UseLookalikeSegmentsSelectionReturn> = ({
  lookalikeSegments = [],
}) => {
  const initialState: LookalikeSegmentsState = useMemo(() => {
    const segmentsByDefinition: Record<string, LookalikeDefinitionSegments> = {};
    const definitionOrder: string[] = [];

    lookalikeSegments.forEach(segment => {
      const definitionId = segment.lookalike_definition_id;

      if (!definitionId) return;

      if (!segmentsByDefinition[definitionId]) {
        segmentsByDefinition[definitionId] = {};
        definitionOrder.push(definitionId);
      }

      if (segment.sub_category === lookalikeSpecificCategory.SeedAggregate) {
        segmentsByDefinition[definitionId].seedSegment = segment;
      } else {
        segmentsByDefinition[definitionId].similaritySegment = segment;
      }
    });

    return { segmentsByDefinition, definitionOrder };
  }, [lookalikeSegments]);

  const [state, dispatch] = useReducer(lookalikeSegmentsReducer, initialState);

  const addSimilaritySegment = useCallback(
    (definitionId: string, segment: LookalikeFormSegment, similarityLevel: SimilarityLevelValue) => {
      // Note: We dispatch AddSimilaritySegment action which will intelligently
      // handle both new additions and updates in the reducer
      dispatch({
        type: ActionType.AddSimilaritySegment,
        definitionId,
        segment: { ...segment, propensity: similarityLevel },
        similarityLevel,
      });
    },
    []
  );

  const editSimilaritySegment = useCallback(
    (definitionId: string, segment: LookalikeFormSegment, similarityLevel: SimilarityLevelValue) => {
      dispatch({
        type: ActionType.EditSimilaritySegment,
        definitionId,
        segment: { ...segment, propensity: similarityLevel },
        similarityLevel,
      });
    },
    []
  );

  const addSeedSegment = useCallback((definitionId: string, segment: LookalikeFormSegment) => {
    dispatch({ type: ActionType.AddSeedSegment, definitionId, segment });
  }, []);

  const removeSeedSegment = useCallback((definitionId: string) => {
    dispatch({ type: ActionType.RemoveSeedSegment, definitionId });
  }, []);

  const removeDefinition = useCallback((definitionId: string) => {
    dispatch({ type: ActionType.RemoveDefinition, definitionId });
  }, []);

  const handleSegmentRemoval = useCallback(
    (segment: FormSegment) => {
      const definitionId = segment.lookalike_definition_id;
      if (!definitionId) return;

      removeDefinition(definitionId);
    },
    [removeDefinition]
  );

  const handleRemoveAll = useCallback(() => {
    dispatch({ type: ActionType.RemoveAll });
  }, []);

  const getSimilarityLevelForDefinition = useCallback(
    (definitionId: string): SimilarityLevelValue | undefined => {
      const definition = state.segmentsByDefinition[definitionId];

      return definition?.similaritySegment?.propensity as SimilarityLevelValue | undefined;
    },
    [state.segmentsByDefinition]
  );

  const hasSeedSegmentForDefinition = useCallback(
    (definitionId: string): boolean => {
      const definition = state.segmentsByDefinition[definitionId];

      return !!definition?.seedSegment;
    },
    [state.segmentsByDefinition]
  );

  const allSelectedSegments = useMemo(() => {
    const segments: LookalikeFormSegment[] = [];

    Object.values(state.segmentsByDefinition).forEach(definition => {
      if (definition.similaritySegment) {
        segments.push(definition.similaritySegment);
      }

      if (definition.seedSegment) {
        segments.push(definition.seedSegment);
      }
    });

    return segments;
  }, [state.segmentsByDefinition]);

  const selectedDefinitionIds = useMemo(() => Object.keys(state.segmentsByDefinition), [state.segmentsByDefinition]);
  const hasSelectedSegments = allSelectedSegments.length > 0;
  const selectedCount = allSelectedSegments.length;

  return {
    segmentsByDefinition: state.segmentsByDefinition,
    definitionOrder: state.definitionOrder,
    allSelectedSegments,
    selectedDefinitionIds,
    hasSelectedSegments,
    selectedCount,
    addSimilaritySegment,
    editSimilaritySegment,
    addSeedSegment,
    removeSeedSegment,
    removeDefinition,
    handleSegmentRemoval,
    handleRemoveAll,
    getSimilarityLevelForDefinition,
    hasSeedSegmentForDefinition,
  };
};
